import type { IntakeSurface } from "./intake-flow-types"

// One table instead of branches: every behavioural difference between the
// three public routes (/go, /embed, /kiosk) is declared here and the flow
// reads it. Embed/widget/kiosk never touch storage — iframes get partitioned
// storage anyway and a kiosk is a shared device.

export type TrackMode = "navigate" | "new-tab" | "none"

export interface SurfaceRules {
  // Write the draft + tracking ref to localStorage (existing keys only).
  persistDraft: boolean
  // How "Track your repair" opens: in-place (first-party), a new tab (iframe),
  // or not at all (kiosk — never leave the consumer's session on the tablet).
  track: TrackMode
  // Show the "leave without sending?" guard when the tab/iframe is closing.
  showLeave: boolean
  // Full shop chrome vs the compact header used inside a third-party page.
  chrome: "full" | "compact"
  // `viewport` pins CTAs/sheets to the viewport; `flow` renders everything
  // in-document so the iframe can auto-size (no fixed UI).
  layout: "viewport" | "flow"
  // Reset to an empty contact screen after done/idle (kiosk).
  autoReset: boolean
}

const FIRST_PARTY: SurfaceRules = {
  persistDraft: true,
  track: "navigate",
  showLeave: true,
  chrome: "full",
  layout: "viewport",
  autoReset: false,
}

const THIRD_PARTY: SurfaceRules = {
  persistDraft: false,
  track: "new-tab",
  showLeave: false,
  chrome: "compact",
  layout: "flow",
  autoReset: false,
}

export const SURFACE_RULES: Record<IntakeSurface, SurfaceRules> = {
  link: FIRST_PARTY,
  qr: FIRST_PARTY,
  sms: FIRST_PARTY,
  embed: THIRD_PARTY,
  widget: THIRD_PARTY,
  kiosk: {
    persistDraft: false,
    track: "none",
    showLeave: false,
    chrome: "full",
    layout: "viewport",
    autoReset: true,
  },
}

// Every "track your repair" entry point goes through this one helper so no
// surface can accidentally link a kiosk tablet to a consumer's tracking page.
// `navigate` = in-app route (react-router); `new-tab` = absolute URL for an
// <a target="_blank"> out of the iframe. Null when tracking must not be offered.
export type TrackLink =
  | { kind: "navigate"; to: string }
  | { kind: "new-tab"; href: string }

export function trackHref(
  surface: IntakeSurface,
  trackToken: string,
  origin: string,
): TrackLink | null {
  const path = `/track/${trackToken}`
  switch (SURFACE_RULES[surface].track) {
    case "navigate":
      return { kind: "navigate", to: path }
    case "new-tab":
      return { kind: "new-tab", href: `${origin}${path}` }
    case "none":
      return null
  }
}
